// src/config/admins.js
// Hub admin model.
//
// Admins are RUNTIME-MANAGED in the `hub_admins` Firestore collection (see
// src/data/admins.js) and editable from the admin UI. In addition, a small set
// of BOOTSTRAP admins is hard-coded here (and mirrored in firestore.rules) so
// there is always an admin who can manage the list and can never be locked out.
//
// IMPORTANT: keep BOOTSTRAP_ADMIN_EMAILS in sync with the hard-coded bootstrap
// email in firestore.rules (hubIsAdmin()).

export const BOOTSTRAP_ADMIN_EMAILS = [
  'lovekush@meltplan.com',
].map((e) => e.toLowerCase());

export function isBootstrapAdmin(email) {
  if (!email) return false;
  return BOOTSTRAP_ADMIN_EMAILS.includes(email.toLowerCase());
}

// Is `email` an admin, given the current runtime allowlist (array of lowercased
// emails from hub_admins)? Bootstrap admins are always admins.
export function isAdminEmail(email, runtimeAdminEmails = []) {
  if (!email) return false;
  const e = email.toLowerCase();
  if (BOOTSTRAP_ADMIN_EMAILS.includes(e)) return true;
  return runtimeAdminEmails.map((x) => (x || '').toLowerCase()).includes(e);
}

export function isMeltplanEmail(email) {
  return typeof email === 'string' && email.toLowerCase().endsWith('@meltplan.com');
}

// ── Scoped admins ────────────────────────────────────────────────────────
// A Hub admin is either:
//   • super  — full access; can manage every resource AND the admin list.
//   • scoped — may only manage resources in an assigned set of `categories`,
//              optionally further narrowed to a set of `teams` (the team limit
//              applies ONLY to Third Party Tools, which is where teams live).
// Bootstrap admins are always super. Legacy hub_admins docs created before this
// feature have no `role` and are treated as super so nothing breaks.

// Resolve the admin record for `email` from the hub_admins rows. Returns a
// synthetic super record for bootstrap admins, or null if the email is not an
// admin at all.
export function getAdminRecord(email, adminRows = []) {
  if (!email) return null;
  const e = email.toLowerCase();
  if (BOOTSTRAP_ADMIN_EMAILS.includes(e)) {
    return { email: e, role: 'super', bootstrap: true, categories: [], teams: [] };
  }
  const row = adminRows.find((r) => (r.email || '').toLowerCase() === e);
  if (!row) return null;
  const role = row.role === 'scoped' ? 'scoped' : 'super'; // legacy docs => super
  return {
    email: e,
    role,
    bootstrap: false,
    categories: Array.isArray(row.categories) ? row.categories : [],
    teams: Array.isArray(row.teams) ? row.teams : [],
  };
}

export function isSuperAdmin(email, adminRows = []) {
  const rec = getAdminRecord(email, adminRows);
  return !!rec && rec.role === 'super';
}

// Can this admin record create / edit / delete the given resource?
// Mirrors the Firestore rules `hubCanManage()` logic exactly.
export function canManageResource(record, resource) {
  if (!record) return false;
  if (record.role === 'super') return true;
  const cat = resource?.category;
  if (!record.categories.includes(cat)) return false;
  // Team limit only narrows Third Party Tools; other categories ignore it.
  if (record.teams && record.teams.length > 0 && cat === 'thirdparty') {
    return record.teams.includes(resource?.team || '');
  }
  return true;
}

// Category keys a record may manage, filtered from `allKeys`. Super => all.
export function allowedCategoryKeys(record, allKeys = []) {
  if (!record) return [];
  if (record.role === 'super') return allKeys;
  return allKeys.filter((k) => record.categories.includes(k));
}
